import { useEffect, useState } from 'react';
import { Position, useKeyPress, useStore, useUpdateNodeInternals } from 'reactflow';
import CustomMovableHandle, { initializeHandlePositionObject } from '../CustomMovableHandle';
import { getNextPortObject, NodeToolbar } from '../NodeToolbar';
import ClialxIcon from '@shared/static/nodes/ClialxIcon';
import MgtaIcon from '@shared/static/nodes/MgtaIcon';
import { Box } from '@mui/material';
import SmtpIcon from '@shared/static/nodes/SmtpIcon';
import TelemetryIcon from '@shared/static/nodes/TelemetryIcon';
import McIcon from '@shared/static/nodes/McIcon';
import NgfwIcon from '@shared/static/nodes/NgfwIcon';
import SwgIcon from '@shared/static/nodes/SwgIcon';
import LoganIcon from '@shared/static/nodes/LoganIcon';
import PcIcon from '@shared/static/nodes/PcIcon';
import PPPoEIcon from '@shared/static/nodes/PPPoEIcon';
import WindowsIcon from '@shared/static/nodes/WindowsIcon';
import AdIcon from '@shared/static/nodes/AdIcon';
import IspIcon1 from '@shared/static/nodes/IspIcon1';
import IspIcon2 from '@shared/static/nodes/IspIcon2';
import IspIntroIcon from '@shared/static/nodes/IspIntroIcon';
import IcapIcon from '@shared/static/nodes/IcapIcon';
import WafIcon from '@shared/static/nodes/WafIcon';
import CollectorIcon from '@shared/static/nodes/CollectorIcon';
import RadiusIcon from '@shared/static/nodes/RadiusIcon';
import TacacsIcon from '@shared/static/nodes/TacacsIcon';
import SiemIcon from '@shared/static/nodes/SiemIcon';
import WebServerIcon from '@shared/static/nodes/WebServerIcon';
import WwwIcon from '@shared/static/nodes/WwwIcon';
import WwwIcon1 from '@shared/static/nodes/WwwIcon1';
import WwwIcon2 from '@shared/static/nodes/WwwIcon2';
import AstraLinuxIcon from '@shared/static/nodes/AstraLinuxIcon';
import KaliLinuxIcon from '@shared/static/nodes/KaliLinuxIcon';
import DcfwIcon from '@shared/static/nodes/DcfwIcon';
import CiscoIcon from '@shared/static/nodes/CiscoIcon';
import TrexIcon from '@shared/static/nodes/TrexIcon';
import RedosIcon from '@shared/static/nodes/RedosIcon';
import FtpIcon from '@shared/static/nodes/FtpIcon';
import VulnerableAppIcon from '@shared/static/nodes/VulnerableAppIcon';
import InternetIcon from '@shared/static/nodes/InternetIcon';
import TestAgentIcon from '@shared/static/nodes/TestAgentIcon';
import { NODE_SIZES } from '@pages/schemes/constants';
import { findOptimalHandlePosition, nodesSelectionActiveSelector } from '@pages/schemes/utils';
import useRotation from '@pages/schemes/hooks/useRotation';
import useSelectionStyle from '@pages/schemes/hooks/useSelectionStyle';
import useDynamicHandlePosition from '@pages/schemes/hooks/useDynamicHandlePosition';
import StorageIcon from '@mui/icons-material/Storage';
import HiddenNodeNameInput from '@pages/schemes/components/Flow/nodes/HiddenNodeNameInput';
import MgtaIntroIcon from '@shared/static/nodes/MgtaIntroIcon';
import WecIcon from '@shared/static/nodes/WecIcon';
import MikrotikIcon from '@shared/static/nodes/MikrotikIcon';
import PtMgmtIcon from '@shared/static/nodes/PtMgmtIcon';
import PtNgfwIcon from '@shared/static/nodes/PtNgfwIcon';
import EncryptorIcon from '@shared/static/nodes/EncryptorIcon';
import TechTourADIcon from '@shared/static/nodes/TechTourADIcon';

const MAP_NODE_TYPE_TO_IMAGE = {
    ngfw: NgfwIcon,
    dcfw: DcfwIcon,
    mc: McIcon,
    logan: LoganIcon,
    waf: WafIcon,
    siem: SiemIcon,
    swg: SwgIcon,
    ubuntu: PcIcon,
    mikrotik: MikrotikIcon,
    cli_a_lx: ClialxIcon,
    mgt_a: MgtaIcon,
    testagent: TestAgentIcon,
    cisco: CiscoIcon,
    trex: TrexIcon,
    astralinux: AstraLinuxIcon,
    kalilinux: KaliLinuxIcon,
    redos: RedosIcon,
    pppoe: PPPoEIcon,
    windows: WindowsIcon,
    encryptor: EncryptorIcon,
    ad: AdIcon,
    tech_tour_ad: TechTourADIcon,
    wec: WecIcon,
    isp1: IspIcon1,
    isp2: IspIcon2,
    isp_intro: IspIntroIcon,
    mgt_a_intro: MgtaIntroIcon,
    telemetryservice: TelemetryIcon,
    smtp: SmtpIcon,
    icap: IcapIcon,
    radius: RadiusIcon,
    tacacs: TacacsIcon,
    webserver: WebServerIcon,
    www: WwwIcon,
    www1: WwwIcon1,
    www2: WwwIcon2,
    vulnerableapp: VulnerableAppIcon,
    internet: InternetIcon,
    ftp: FtpIcon,
    collector: CollectorIcon,
    ptmgmt: PtMgmtIcon,
    ptngfw: PtNgfwIcon,
};

const HANDLES_POOL = ['port0', 'port1', 'port2', 'port3', 'port4', 'port5', 'port6', 'port7', 'port8', 'port9'];

export function NodeWithMultipleHandles({ id, data, selected, isConnectable, nodeType }) {
    const isNodeDraggable = useStore((state) => state.nodesDraggable);
    const [, rotation] = useRotation(data, id);
    const updateNodeInternals = useUpdateNodeInternals();
    const selectionStyle = useSelectionStyle(selected);
    const [selectedHandleId, setSelectedHandleId] = useState(data.selectedHandleId);
    const [existingPorts, setExistingPorts] = useState(data?.ports ?? []);
    const isNodesSelectionActive = useStore(nodesSelectionActiveSelector);
    const addNewPortShortcutPressed = useKeyPress('Control+Equal');
    const deletePortShortcutPressed = useKeyPress('Control+Minus');

    useEffect(() => {
        setExistingPorts(data?.ports ?? []);
    }, [data?.ports]);

    useEffect(() => {
        if (isNodesSelectionActive || !selected) {
            return;
        }

        if (addNewPortShortcutPressed) {
            const newHandleObject = getNextPortObject(data, HANDLES_POOL, 1);

            if (newHandleObject) {
                handleAddPortButton(newHandleObject);
            }
        } else if (deletePortShortcutPressed) {
            const lastPortName = data.ports.at(-1)?.name;
            handleDeletePortButton(lastPortName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addNewPortShortcutPressed, deletePortShortcutPressed]);

    // alternate every side of the node for every new port added
    const [handle_0_Position, setInitialHandle_0_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[0],
        Position.Top,
    );
    const [handle_1_Position, setInitialHandle_1_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[1],
        Position.Right,
    );
    const [handle_2_Position, setInitialHandle_2_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[2],
        Position.Bottom,
    );
    const [handle_3_Position, setInitialHandle_3_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[3],
        Position.Left,
    );
    const [handle_4_Position, setInitialHandle_4_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[4],
        Position.Top,
    );
    const [handle_5_Position, setInitialHandle_5_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[5],
        Position.Right,
    );
    const [handle_6_Position, setInitialHandle_6_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[6],
        Position.Bottom,
    );
    const [handle_7_Position, setInitialHandle_7_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[7],
        Position.Left,
    );
    const [handle_8_Position, setInitialHandle_8_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[8],
        Position.Top,
    );
    const [handle_9_Position, setInitialHandle_9_Position] = useDynamicHandlePosition(
        rotation,
        data,
        HANDLES_POOL[9],
        Position.Right,
    );

    const mapHandleIdToUseDynamicHandlePosition = {
        [HANDLES_POOL[0]]: [handle_0_Position, setInitialHandle_0_Position],
        [HANDLES_POOL[1]]: [handle_1_Position, setInitialHandle_1_Position],
        [HANDLES_POOL[2]]: [handle_2_Position, setInitialHandle_2_Position],
        [HANDLES_POOL[3]]: [handle_3_Position, setInitialHandle_3_Position],
        [HANDLES_POOL[4]]: [handle_4_Position, setInitialHandle_4_Position],
        [HANDLES_POOL[5]]: [handle_5_Position, setInitialHandle_5_Position],
        [HANDLES_POOL[6]]: [handle_6_Position, setInitialHandle_6_Position],
        [HANDLES_POOL[7]]: [handle_7_Position, setInitialHandle_7_Position],
        [HANDLES_POOL[8]]: [handle_8_Position, setInitialHandle_8_Position],
        [HANDLES_POOL[9]]: [handle_9_Position, setInitialHandle_9_Position],
    };

    const handleAddPortButton = (newPort) => {
        if (existingPorts.length > 9) {
            return;
        }

        let newPortId;
        setExistingPorts((existingPorts) => {
            newPortId = newPort['name'];
            const newExistingPorts = [...existingPorts, newPort];
            data.ports = newExistingPorts;
            const optimalHandlePosition = findOptimalHandlePosition(data);

            initializeHandlePositionObject(
                data,
                optimalHandlePosition,
                newPortId,
                NODE_SIZES[nodeType].width,
                NODE_SIZES[nodeType].height,
            );

            const setInitialHandlePosition = mapHandleIdToUseDynamicHandlePosition[newPortId][1];
            setInitialHandlePosition(optimalHandlePosition);

            data.ports_positions[newPortId].reactflow_position = optimalHandlePosition;
            return newExistingPorts;
        });

        // setTimeout to correct execution order
        setTimeout(() => {
            setSelectedHandleId(newPortId);
            data.selectedHandleId = newPortId;
            updateNodeInternals(id);
        }, 0);
    };

    const handleDeletePortButton = (portNameToDelete) => {
        if (existingPorts.length === 1) {
            return;
        }

        setExistingPorts((existingPorts) => {
            const newExistingPorts = existingPorts.filter((ep) => ep['name'] !== portNameToDelete);

            //delete port from data object
            data.ports = newExistingPorts;
            delete data.ports_positions[portNameToDelete];
            return newExistingPorts;
        });
        updateNodeInternals(id);
    };

    const handleUpdateMemory = (newMemoryValue) => {
        data.memory = newMemoryValue;
    };

    const handleUpdateCpu = (newCpuValue) => {
        data.cpu = newCpuValue;
    };

    const handleUpdateUserCount = (newUserCountValue) => {
        data['user_count'] = newUserCountValue;
    };

    const handleUpdateGroupCount = (newGroupCountValue) => {
        data['group_count'] = newGroupCountValue;
    };

    const handleUpdateWords = (newWordsValue) => {
        data['words'] = newWordsValue;
    };
    const handleUpdateLoganBasedRole = (newLoganRole) => {
        data['role'] = newLoganRole;
    };

    const handleUpdateConfig = (newConfigValue) => {
        data['config'] = newConfigValue;
    };

    const handleUpdateConfigName = (newConfigNameValue) => {
        data['configName'] = newConfigNameValue;
    };

    const handleUpdateTrexConfig = (newTrexConfigValue) => {
        data['trexConfig'] = newTrexConfigValue;
    };

    const NodeIcon = MAP_NODE_TYPE_TO_IMAGE[nodeType];

    return (
        <Box
            sx={{
                ...selectionStyle,
                maxWidth: NODE_SIZES[nodeType].width,
                maxHeight: NODE_SIZES[nodeType].height,
                minWidth: NODE_SIZES[nodeType].width,
                minHeight: NODE_SIZES[nodeType].height,
                // transform: rotation ? `rotate(${rotation}deg)` : '',
                padding: '2px',
                position: 'relative',
            }}
        >
            {data['hw_id'] && (
                <StorageIcon color="action" fontSize="0.8rem" sx={{ position: 'absolute', bottom: -5, right: -15 }} />
            )}
            <Box sx={{ maxWidth: '100%', maxHeight: '100%' }}>{NodeIcon && <NodeIcon />}</Box>
            {existingPorts?.map((port) => (
                <CustomMovableHandle
                    key={port['name']}
                    nodeType={nodeType}
                    nodeId={id}
                    nodeSelected={selected}
                    selectedHandleId={selectedHandleId}
                    setSelectedHandleId={setSelectedHandleId}
                    handleId={port['name']}
                    position={mapHandleIdToUseDynamicHandlePosition[port['name']][0]}
                    setInitialHandlePosition={mapHandleIdToUseDynamicHandlePosition[port['name']][1]}
                    isConnectable={port.isConnectable ?? isConnectable}
                    data={data}
                    label={port['name']}
                    movable={isNodeDraggable}
                />
            ))}
            {selected && (
                <NodeToolbar
                    nodeId={id}
                    hidden={!selected}
                    handleAddPortButton={handleAddPortButton}
                    handleDeletePortButton={handleDeletePortButton}
                    NodeConfigMenuProps={{
                        nodesParamsCustomisable: true,
                        configureOnly: !isNodeDraggable,
                        updateMemory: handleUpdateMemory,
                        updateCpu: handleUpdateCpu,
                        updateUserCount: handleUpdateUserCount,
                        updateGroupCount: handleUpdateGroupCount,
                        updateWords: handleUpdateWords,
                        updateLoganBasedRole: handleUpdateLoganBasedRole,
                        updateConfig: handleUpdateConfig,
                        updateConfigName: handleUpdateConfigName,
                        updateTrexConfig: handleUpdateTrexConfig,
                    }}
                    HANDLES_POOL={HANDLES_POOL}
                />
            )}
            <HiddenNodeNameInput nodeId={id} nodeData={data} nodeSelected={selected} />
        </Box>
    );
}
